<template>
  <div class="section">
    <b-loading v-if="isloading" :is-full-page="true"/>

    <div v-else class="container">
      <div class="buttons is-right">
        <b-button type="is-primary" :disabled="!hasShippings" @click="exportScanShippings">
          {{ $t('global.export') }}
        </b-button>
      </div>

      <ag-grid-vue
        style="width: 100%; height: 1000px;"
        class="ag-theme-alpine"
        :modules="modules"
        :pagination="true"
        :pagination-auto-page-size="true"
        :grid-options="gridOptions"
        :row-data="shippings"
        :column-defs="columnDefs"
        @row-clicked="showBarcodes"
      />

      <b-modal :active.sync="showingBarcodes" scroll="keep" has-modal-card>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">
              {{ selectedShippingTitle }}
            </p>
          </header>
          <section class="modal-card-body">
            <div v-if="selectedBarcodes.length" class="barcode-list">
              <span
                v-for="barcode in selectedBarcodes"
                :key="barcode"
                class="tag is-link is-light barcode-tag"
              >
                {{ barcode }}
              </span>
            </div>
            <p v-else>
              No barcodes
            </p>
          </section>
          <footer class="modal-card-foot">
            <button class="button is-fullwidth" @click="showingBarcodes = false">
              {{ $t('global.close') }}
            </button>
          </footer>
        </div>
      </b-modal>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import XLSX from 'xlsx'
import ShippingManagement from '@/services/v2/shippingManagement'
import Customer from '@/services/v2/customer'

export default {
  data () {
    return {
      modules: AllModules,
      groupDisplayType: 'custom',
      isloading: false,
      shippings: [],
      dynamicFields: [],
      selectedShipping: null,
      showingBarcodes: false
    }
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
      selectedCustomer: state => state.selectedCustomer
    }),
    gridOptions () {
      return {
        defaultColDef: {
          sortable: true,
          filter: true,
          resizable: true
        },
        sideBar: 'columns',
        getContextMenuItems: this.getContextMenuItems,
        rowClass: 'clickable-row'
      }
    },
    columnDefs () {
      return [
        {
          headerName: this.$t('customer.shipping_management.date_table.date'),
          field: 'date',
          filter: 'agDateColumnFilter',
          filterParams: {
            comparator: this.compareDateFilter,
            suppressAndOrCondition: true
          }
        },
        {
          headerName: this.$t('customer.shipping_management.date_table.date') + ' (full)',
          field: 'fullDate',
          hide: true,
          filter: 'agDateColumnFilter',
          filterParams: {
            comparator: this.compareDateFilter,
            suppressAndOrCondition: true
          }
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.consignee'),
          field: 'consigneeCode',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.enduserName'),
          field: 'enduserName',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.enduserAddress'),
          field: 'enduserAddress',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.reference'),
          field: 'reference',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        ...this.dynamicFields.map(f => ({
          headerName: this.translateDynamicField(f),
          field: `dynamic_${f.name}`,
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        })),
        {
          headerName: this.$t('customer.shipping_management.hours_table.barcode_qty'),
          field: 'quantity',
          filter: 'agNumberColumnFilter'
        },
        {
          headerName: this.$t('customer.shipping_management.hours_table.reference_complement'),
          field: 'referenceComplement',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        }
      ]
    },
    hasShippings () {
      return !!(this.shippings && this.shippings.length)
    },
    selectedBarcodes () {
      if (!this.selectedShipping || !this.selectedShipping.crateMovements) return []
      return this.selectedShipping.crateMovements
        .map(crateMovement => crateMovement.barcode)
        .filter(Boolean)
    },
    selectedShippingTitle () {
      if (!this.selectedShipping) return this.$t('sidebar.scan_shipping')
      const reference = this.selectedShipping.reference || this.$t('customer.shipping_management.hours_table.unspecified')
      return `${reference} - ${this.selectedShipping.quantity} ${this.$t('customer.shipping_management.hours_table.barcode_qty')}`
    }
  },
  watch: {
    selectedCustomer (newValue, oldValue) {
      this.loadData()
    }
  },
  mounted () {
    this.loadData()
  },
  methods: {
    async loadData () {
      if (!this.selectedCustomer) return
      this.isloading = true
      try {
        const dynamicFields = await Customer.getDynamicFields(this.selectedCustomer)
        this.dynamicFields = dynamicFields?.filter(df => df.active) || []

        const [shippings, crateMovements] = await Promise.all([
          ShippingManagement.getShippings(this.selectedCustomer),
          ShippingManagement.getCrateMovementsByCustomerId(this.selectedCustomer)
        ])

        this.shippings = (shippings || []).map(shipping => {
          const shippingCrateMovements = (crateMovements || []).filter(crateMovement => {
            return crateMovement.shipping_id === shipping.id || crateMovement.shippingId === shipping.id
          })
          const createDate = shipping.creationDate || shipping.creation_date

          const dynamicValues = {}
          for (const fv of (shipping.dynamicFieldValues || [])) {
            dynamicValues[`dynamic_${fv.name}`] = fv.value
          }

          return {
            ...shipping,
            createDate,
            date: this.formatLocalDate(createDate),
            fullDate: this.formatLocalDateTime(createDate),
            crateMovements: shippingCrateMovements,
            quantity: shippingCrateMovements.length,
            ...dynamicValues
          }
        }).sort((a, b) => new Date(b.createDate) - new Date(a.createDate))
      } catch (error) {
        this.$buefy.toast.open({
          message: this.$t('customer.scan_mvt.errors.load_data'),
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.isloading = false
      }
    },
    formatLocalDateTime (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleString(navigator.language)
    },
    formatLocalDate (value) {
      if (!value) return ''
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return ''
      return date.toLocaleDateString(navigator.language)
    },
    compareDateFilter (filterLocalDateAtMidnight, cellValue) {
      const cellDate = new Date(cellValue)
      if (Number.isNaN(cellDate.getTime())) return -1

      const cellDateAtMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate())
      if (cellDateAtMidnight.getTime() === filterLocalDateAtMidnight.getTime()) return 0
      return cellDateAtMidnight < filterLocalDateAtMidnight ? -1 : 1
    },
    showBarcodes (params) {
      if (!params || !params.data) return
      this.selectedShipping = params.data
      this.showingBarcodes = true
    },
    getContextMenuItems (params) {
      const menuItems = (params.defaultItems || []).filter(item => item !== 'export')

      if (!params.node || !params.node.data) return menuItems

      return [
        ...menuItems,
        'separator',
        {
          name: this.$t('global.export'),
          action: () => this.exportSelectedScanShipping(params.node.data)
        }
      ]
    },
    exportScanShippings () {
      if (!this.hasShippings) return

      this.writeScanShippingsExport(this.shippings)
    },
    exportSelectedScanShipping (shipping) {
      if (!shipping) return
      this.writeScanShippingsExport([shipping])
    },
    exportColumns () {
      return [
        { header: this.$t('customer.shipping_management.date_table.date'), field: 'creationDate' },
        { header: this.$t('customer.shipping_management.edit_modal.crates'), field: 'barcodes' },
        { header: this.$t('customer.shipping_management.hours_table.consignee'), field: 'consigneeCode' },
        { header: this.$t('customer.shipping_management.hours_table.enduserName'), field: 'enduserName' },
        { header: this.$t('customer.shipping_management.hours_table.enduserAddress'), field: 'enduserAddress' },
        { header: this.$t('customer.shipping_management.hours_table.reference'), field: 'reference' },
        { header: this.$t('customer.shipping_management.hours_table.reference_complement'), field: 'referenceComplement' },
        ...this.dynamicFields.map(f => ({
          header: this.translateDynamicField(f),
          field: `dynamic_${f.name}`
        }))
      ]
    },
    translateDynamicField (field) {
      const key = `customer.shipping_management.date_table.${field.translateRef}`
      return this.$te(key) ? this.$t(key) : field.displayName
    },
    writeScanShippingsExport (shippings) {
      const columns = this.exportColumns()
      const data = [
        columns.map(column => column.header),
        ...this.buildExportRows(columns, shippings)
      ]
      const worksheet = XLSX.utils.aoa_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      const fileDate = new Date().toISOString().slice(0, 10)

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Scan shipping')
      XLSX.writeFile(workbook, `GPS-${this.$t('sidebar.scan_shipping')}-${fileDate}.csv`)
    },
    buildExportRows (columns, shippings) {
      return shippings.flatMap(shipping => {
        const barcodes = this.exportBarcodes(shipping)
        const rowBarcodes = barcodes.length ? barcodes : ['']

        return rowBarcodes.map(barcode => {
          return columns.map(column => this.formatExportValue(shipping, column.field, barcode))
        })
      })
    },
    exportBarcodes (shipping) {
      return (shipping.crateMovements || []).map(crateMovement => crateMovement.barcode).filter(Boolean)
    },
    formatExportValue (shipping, field, barcode) {
      if (field === 'barcodes') return barcode || ''
      if (field === 'creationDate') return shipping.date
      return shipping[field] || ''
    }
  }
}
</script>

<style scoped>
.barcode-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.barcode-tag {
  margin: 0;
}

::v-deep .clickable-row {
  cursor: pointer;
}
</style>
